/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { createDialogRoute, DateTimeTypes, NumberTypes, TextTypes } from '@gh/common-shared';
import { DialogInput, NotificationService } from '@gh/core-ui';
import {
  JeAddOrders,
  JeOrderForSchedule,
  validateJeAddOrderStageField,
} from '../../../resources/job-estimate-details.model';
import {
  buildDateTimeColumn,
  buildEditableDateTimeColumn,
  buildEditableTextColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { code3Formatter, createDefaultGridModel } from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import { DateFormat, emptySelectionModel, SelectionModel } from '@gh/core-data';
import { MatDialogRef } from '@angular/material/dialog';
import { isDate } from 'lodash';
import { normalizeDate, setHours, setMinutes } from '@gh/core-util';
import { WORK_TIME_HOURS_START } from '@gh/builder-shared-gantt-chart';

@Component({
  standalone: false,
  selector: 'gh-je-add-orders-dialog',
  templateUrl: 'je-add-orders-dialog.component.html',
  styleUrls: ['je-add-orders-dialog.component.scss'],
})
export class JeAddOrdersDialogComponent implements OnInit, DialogInput<JeAddOrders> {
  input: JeAddOrders;
  gridDefinition: GridDefinition;
  items: JeOrderForSchedule[] = [];
  selectionModel: SelectionModel;

  get hasSelected(): boolean {
    if (this.selectionModel.isExcludeMode) {
      return !!this.items.length && this.selectionModel.idList.length < this.items.length;
    }
    return !!this.selectionModel.idList.length;
  }

  constructor(
    private dialogRef: MatDialogRef<any>,
    private translationService: TranslationService,
    private notification: NotificationService,
    private gridDefinitionFactory: GridDefinitionFactory,
  ) {}

  ngOnInit(): void {
    this.selectionModel = emptySelectionModel();
    const newStartDate = setMinutes(setHours(normalizeDate(new Date()), WORK_TIME_HOURS_START), 0, 0, 0);
    this.items = this.input.jobOrdersForSchedule.map((item) => {
      const deliveryDate = item.deliveryDate ? setHours(item.deliveryDate, WORK_TIME_HOURS_START) : undefined;
      return {
        ...item,
        deliveryDate,
        startDate: item.startDate ?  setHours(item.startDate, WORK_TIME_HOURS_START) : deliveryDate || newStartDate,
      };
    });
    const model = createDefaultGridModel({
                                           editable: true,
                                           getId: (item) => item.jobOrderId,
                                           validate: validateJeAddOrderStageField,
                                           command: (entity, name, value) => this.setField(entity, name, value),
                                         });
    this.gridDefinition = this.gridDefinitionFactory.buildManualWithModel(
      {
        model,
        options: {
          rowSelection: { mode: 'multiRow', enableClickSelection: true, checkboxes: false, headerCheckbox: false },
          defaultColDef: {
            sortable: true,
          },
        },
        columns: [
          {
            field: 'costCenterCode',
            headerName: this.translationService.get('lbl_cc_number'),
            width: 100,
            valueFormatter: code3Formatter,
          }, {
            field: 'costCenterName',
            ...buildEditableTextColumn(TextTypes.Text64,  model.getCellAccessor('costCenterName')),
            headerName: this.translationService.get('lbl_task_name'),
            width: 200,
            cellClassRules : {
              'is-invalid': ({ data }: { data: JeOrderForSchedule }) => !this.textValid(data.costCenterName),
            },
          }, {
            field: 'startDate',
            headerName: this.translationService.get('lbl_start_date'),
            width: 130,
            ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('startDate'), {
              format: DateFormat.Date,
              forceClearable: true,
            }),
            cellClassRules : {
              'is-invalid': ({ data }: { data: JeOrderForSchedule }) => !this.isDateValid(data.startDate),
            },
          },
          {
            field: 'orderNumber',
            headerName: this.translationService.get('lbl_order'),
            width: 70,
          },
          {
            field: 'supplierName',
            headerName: this.translationService.get('lbl_contact_creditor_supplier'),
            width: 200,
          },
          {
            field: 'deliveryDate',
            ...buildDateTimeColumn(DateTimeTypes.ShortDate),
            headerName: this.translationService.get('lbl_delivery_date'),
            width: 130,
          },
          {
            field: 'amount',
            headerName: this.translationService.get('lbl_amount_dollar'),
            ...buildNumberColumn(NumberTypes.Price),
            width: 100,
          },
        ],
      });
  }

  onSelectionModelChanged(model: SelectionModel): void {
    this.selectionModel = model;
  }

  onApply(): void {
    const data = this.getSelected();
    const isValid = this.validate(data);
    if (isValid) {
      this.dialogRef.close(data);
    }
  }

  onCancel(): void {
    this.dialogRef.close(void 0);
  }

  getItemId(item: JeOrderForSchedule): string {
    return item.jobOrderId;
  }

  isDateValid(date?: Date): boolean {
    return !!date && isDate(date);
  }

  textValid(text?: string): boolean {
    return !!text && !!text.trim().length;
  }

  getSelected(): JeOrderForSchedule[] {
    if (this.selectionModel.isExcludeMode) {
      return this.items.filter((item) => !this.selectionModel.idList.includes(this.getItemId(item)));
    }
    return this.items.filter((item) => this.selectionModel.idList.includes(this.getItemId(item)));
  }

  private validate(data: JeOrderForSchedule[]): boolean {
    let isValid = true;
    data.forEach((item) => {
      if (!this.textValid(item.costCenterName)) {
        isValid = false;
      }
      if (!this.isDateValid(item.startDate)) {
        isValid = false;
      }
    });
    if (!isValid) {
      this.notification.error('msg_field_required');
    }
    return isValid;
  }

  private setField(entity: JeOrderForSchedule, name: string, value: any): void {
    if (name === 'startDate' && value) {
      value = setMinutes(setHours(normalizeDate(value), WORK_TIME_HOURS_START), 0, 0, 0);
    }
    this.items.forEach((item) => {
      if (item.jobOrderId === entity.jobOrderId) {
        item[name] = value;
      }
    });
  }
}

export const jeAddOrdersDialogRoute = createDialogRoute({
  component: JeAddOrdersDialogComponent,
});
